import React, { useEffect, useState } from 'react';

import {
  FetchPurchaseOrdersParams,
  fetchPurchaseOrders,
  getPurchaseOrderCounts,
} from 'api/purchaseOrder';
import { useQuery } from 'react-query';
import { washHub } from 'store/slices';
import { PurchaseOrderFiltersValues } from 'components/modules/PurchaseOrderFilters';

import { PurchaseOrdersProps } from './PurchaseOrders.props';
import PurchaseOrdersView from './PurchaseOrders.view';

const PurchaseOrdersContainer = (_: PurchaseOrdersProps) => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<number | undefined>(undefined);
  const [filters, setFilters] = useState<PurchaseOrderFiltersValues>({});

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();

  const tab: FetchPurchaseOrdersParams['tab'] =
    defaultIndex === 1 ? 'active' : defaultIndex === 2 ? 'completed' : 'all';

  const listParams: FetchPurchaseOrdersParams = {
    page,
    limit: 10,
    washHub: washHubId,
    search: search || undefined,
    manufacturerId: filters.manufacturerId?.length
      ? filters.manufacturerId
      : undefined,
    destinationLocationId: filters.destinationLocationId?.length
      ? filters.destinationLocationId
      : undefined,
    skuId: filters.skuId?.length ? filters.skuId : undefined,
    tab,
    ...(tab === 'completed'
      ? { status: 'paid' }
      : {
          status: filters.status?.length ? filters.status : undefined,
          ...(tab === 'active' ? { excludeStatus: 'paid' } : {}),
        }),
    sortByField: sortBy,
    sort,
  };

  const { data, isLoading, isFetching } = useQuery(
    [
      'fetch-purchase-orders',
      washHubId,
      page,
      search,
      defaultIndex,
      sortBy,
      sort,
      filters.status?.toString(),
      filters.manufacturerId?.toString(),
      filters.destinationLocationId?.toString(),
      filters.skuId?.toString(),
    ],
    () => fetchPurchaseOrders(listParams),
    {
      enabled: isWashHubIdValid,
      keepPreviousData: true,
    },
  );

  const { data: counts } = useQuery(
    [
      'fetch-purchase-order-counts',
      washHubId,
      search,
      filters.status?.toString(),
      filters.manufacturerId?.toString(),
      filters.destinationLocationId?.toString(),
      filters.skuId?.toString(),
    ],
    () =>
      getPurchaseOrderCounts({
        washHub: washHubId,
        search: search || undefined,
        status: filters.status?.length ? filters.status : undefined,
        manufacturerId: filters.manufacturerId?.length
          ? filters.manufacturerId
          : undefined,
        destinationLocationId: filters.destinationLocationId?.length
          ? filters.destinationLocationId
          : undefined,
        skuId: filters.skuId?.length ? filters.skuId : undefined,
      }),
    {
      enabled: isWashHubIdValid,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    setPage(1);
  }, [
    washHubId,
    search,
    defaultIndex,
    sortBy,
    sort,
    filters.status?.toString(),
    filters.manufacturerId?.toString(),
    filters.destinationLocationId?.toString(),
    filters.skuId?.toString(),
  ]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const timeoutId = window.setTimeout(() => {
      if (trimmed.length > 2) {
        setSearch(trimmed);
        return;
      }

      setSearch('');
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const onApplyFilters = (nextFilters: PurchaseOrderFiltersValues) => {
    setFilters({
      status: nextFilters.status?.length ? nextFilters.status : undefined,
      manufacturerId: nextFilters.manufacturerId?.length
        ? nextFilters.manufacturerId
        : undefined,
      destinationLocationId: nextFilters.destinationLocationId?.length
        ? nextFilters.destinationLocationId
        : undefined,
      skuId: nextFilters.skuId?.length ? nextFilters.skuId : undefined,
    });
  };

  const onClearFilters = () => {
    setFilters({});
  };

  const onSort = (field: 'createdAt' | 'updatedAt') => {
    if (sortBy === field) {
      setSort((current) => (current === undefined || current === 1 ? -1 : 1));
      return;
    }

    setSortBy(field);
    setSort(-1);
  };

  return (
    <PurchaseOrdersView
      purchaseOrders={data?.items || []}
      isLoading={isLoading || isFetching}
      pagination={{
        page,
        setPage,
        totalCount: data?.total || 0,
        totalPages: data?.totalPages || 0,
      }}
      filters={filters}
      search={searchInput}
      setSearch={setSearchInput}
      defaultIndex={defaultIndex}
      setDefaultIndex={setDefaultIndex}
      counts={{
        all: counts?.all ?? 0,
        active: counts?.active ?? 0,
        completed: counts?.completed ?? 0,
      }}
      sortBy={sortBy}
      sort={sort}
      onSort={onSort}
      onApplyFilters={onApplyFilters}
      onClearFilters={onClearFilters}
    />
  );
};

export default PurchaseOrdersContainer;
